<div class="pb-12">
  <div class="max-content-width spaced-sections">
    <div class="flex justify-between items-center gap-4 mb-4">
      <div>
        <button
          type="button"
          (click)="doGoBack()"
          class="mb-1 text-sm text-secondary hover:underline flex items-center gap-1 mt-1"
        >
          ← Back to projects
        </button>
        <h1 class="page-title">{{ project()?.title || 'Loading…' }}</h1>
        @if (project(); as p) {
          <p class="text-sm mt-1">
            <span
              class="align-middle"
              [class.italic]="!p.description"
              [class.truncate]="!descriptionExpanded()"
              [class.max-w-2xl]="!descriptionExpanded()"
              [class.inline-block]="!descriptionExpanded()"
              [class.text-slate-700]="p.description"
              [class.text-gray-400]="!p.description"
            >
              {{ p.description || 'No description provided' }}
            </span>
            @if (p.description) {
              <button
                type="button"
                (click)="descriptionExpanded.set(!descriptionExpanded())"
                class="inline-flex items-center gap-1 text-xs text-secondary hover:underline align-middle"
                [attr.aria-expanded]="descriptionExpanded()"
                [attr.aria-label]="
                  descriptionExpanded() ? 'Collapse description' : 'Expand description'
                "
              >
                <svg
                  class="w-3 h-3 transition-transform duration-200"
                  [class.rotate-180]="descriptionExpanded()"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                {{ descriptionExpanded() ? 'Show less' : 'Show more' }}
              </button>
            }
          </p>
          <div class="flex items-center gap-3 mt-2 flex-wrap font-extrabold">
            <span [class]="getStageStateClass(p.state)">
              {{ formatStageState(p.state) }}
            </span>
            <span class="text-gray-400">•</span>
            @if (group(); as g) {
              <app-group-hover-popup [group]="g">
                <span class="text-sm text-blue-600 font-medium cursor-pointer hover:underline">
                  {{ p.groupName }}
                </span>
              </app-group-hover-popup>
              <span class="text-gray-400">•</span>
            }
            <span class="text-sm text-gray-700 font-medium"
              >📆 {{ p.createdAt | date: 'MMM d, yyyy' }}</span
            >
          </div>
        }
      </div>
      @if (project(); as p) {
        <div class="flex gap-2 items-center">
          <button
            type="button"
            (click)="openGuide()"
            class="inline-flex items-center justify-center rounded-lg transition-colors font-semibold w-9 h-9 border border-slate-200 bg-white text-slate-400 hover:text-secondary hover:border-secondary text-sm cursor-pointer"
            aria-label="Open project guide"
          >
            ?
          </button>
          <button
            type="button"
            (click)="showAuditTrail.set(true)"
            class="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Audit Trail
          </button>
          @if (isProjectPI()) {
            <button type="button" (click)="showEditModal.set(true)" class="btn-medium-text">
              Edit
            </button>
          }
          @if (isProjectResearcher()) {
            <button
              type="button"
              (click)="onLeaveProject()"
              class="inline-flex items-center justify-center rounded-lg transition-colors font-semibold px-3 py-2 text-sm border border-red-300 bg-white text-red-600 hover:bg-red-50 min-w-16 text-nowrap"
            >
              Leave
            </button>
          }
          @if (showAnnotatorButton(p)) {
            <button
              type="button"
              (click)="navigateToAnnotator()"
              class="btn-primary flex items-center gap-2"
            >
              Annotate
            </button>
          }
        </div>
      }
    </div>

    @if (project(); as p) {
      <app-project-stage-nav
        [projectId]="projectId"
        [projectState]="p.state"
        [currentPageStage]="currentStage()"
        [userRole]="p.userRole ?? 'NONE'"
      ></app-project-stage-nav>
    }

    <router-outlet></router-outlet>
  </div>
</div>

@if (project()) {
  <app-project-edit-modal
    [open]="showEditModal()"
    [project]="project()!"
    (openChange)="showEditModal.set($event)"
    (saved)="onProjectUpdated($event)"
    (deleted)="onProjectDeleted()"
  ></app-project-edit-modal>
}

<app-project-extraction-questions
  [open]="ctx.showExtractionQuestions()"
  [projectId]="projectId"
  (openChange)="ctx.closeExtractionQuestions()"
></app-project-extraction-questions>

<app-confirm-modal
  [open]="showLeaveConfirm"
  [title]="'Leave project?'"
  [message]="leaveMessage"
  [confirmText]="'Leave project'"
  [cancelText]="'Cancel'"
  [confirmVariant]="'danger'"
  [loading]="leaveLoading"
  size="sm"
  (openChange)="showLeaveConfirm = $event"
  (confirmed)="confirmLeaveProject()"
  (canceled)="cancelLeaveProject()"
></app-confirm-modal>

@if (leaveError) {
  <div class="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
    <p class="text-sm text-red-700">{{ leaveError }}</p>
  </div>
}

<app-project-guide
  [open]="showGuide()"
  [projectState]="currentStage()"
  [guideAnchor]="guideAnchor()"
  (openChange)="closeGuide()"
></app-project-guide>

@if (showAuditTrail()) {
  <app-audit-trail
    [open]="showAuditTrail()"
    [initialEntityId]="auditTrailPaperId()"
    (openChange)="onAuditTrailChange($event)"
  ></app-audit-trail>
}
